import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const hasNativeBridge = typeof window.BrainSyncVoice?.isAvailable === 'function';

export function VoiceButton({ onResult, onInterim, autoSubmit = false, className = '', disabled = false, 'data-testid': testId = 'voice-search-btn' }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const supported = !!SpeechRecognition || hasNativeBridge;

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  // Listen for native voice results from Android bridge
  useEffect(() => {
    if (!hasNativeBridge) return;
    const handler = (e) => {
      const { text, cancelled } = e.detail || {};
      setListening(false);
      if (text && !cancelled) {
        onResult(text.trim(), autoSubmit);
      }
    };
    window.addEventListener('brainsync-voice-result', handler);
    return () => window.removeEventListener('brainsync-voice-result', handler);
  }, [onResult, autoSubmit]);

  const start = useCallback(() => {
    if (disabled) return;

    // Prefer native Android bridge in TWA
    if (hasNativeBridge) {
      setListening(true);
      try { window.BrainSyncVoice.startListening(); } catch (e) { setListening(false); }
      return;
    }

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';
    try { recognition.maxAlternatives = 1; } catch (e) { /* ignore */ }

    recognition.onresult = (event) => {
      let interim = '';
      let final_ = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final_ += transcript;
        } else {
          interim += transcript;
        }
      }
      if (interim && onInterim) onInterim(interim);
      if (final_) {
        onResult(final_.trim(), autoSubmit);
        stop();
      }
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        // Speech recognition error
      }
      stop();
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [disabled, onResult, onInterim, autoSubmit, stop]);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
    } else {
      start();
    }
  }, [listening, start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
    };
  }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className={`relative flex items-center justify-center transition-all duration-200 ${className}`}
      title={listening ? 'Stop listening' : 'Voice search'}
      data-testid={testId}
    >
      {listening ? (
        <>
          <span className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
          <MicOff className="w-4 h-4 text-red-500 relative z-10" />
        </>
      ) : (
        <Mic className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
      )}
    </button>
  );
}
